import {
  Group,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  RingGeometry,
  type BufferGeometry,
} from 'three';
import type { GameSnapshot, PlayerAction } from '../core/types';
import type { ActionEffectStyle, RingEffectStyle, SlideTrailStyle } from './visual-presets';

type EffectKind = 'ring' | 'trail';

interface EffectInstance {
  readonly kind: EffectKind;
  readonly mesh: Mesh<BufferGeometry, MeshBasicMaterial>;
  active: boolean;
  startedAt: number;
  duration: number;
  startScaleX: number;
  startScaleY: number;
  endScaleX: number;
  endScaleY: number;
  startOpacity: number;
  drift: number;
  startZ: number;
}

export class ActionEffects {
  public readonly group = new Group();
  private readonly instances: EffectInstance[] = [];
  private previousAction: PlayerAction = 'grounded';
  private previousElapsed = 0;
  private lastSlideDistance = 0;
  private initialized = false;

  public constructor(private readonly style: ActionEffectStyle) {
    this.group.name = 'action-effects';
  }

  public update(snapshot: Readonly<GameSnapshot>): void {
    if (snapshot.elapsed < this.previousElapsed) this.reset();
    if (!this.initialized) {
      this.previousAction = snapshot.player.action;
      this.lastSlideDistance = snapshot.distance;
      this.initialized = true;
    }

    if (this.previousAction !== 'jumping' && snapshot.player.action === 'jumping') {
      this.spawnRing(snapshot.player.x, snapshot.elapsed, this.style.jumpDust);
    }
    if (this.previousAction === 'jumping' && snapshot.player.action === 'grounded') {
      this.spawnRing(snapshot.player.x, snapshot.elapsed, this.style.landingDust);
    }

    if (snapshot.player.action === 'sliding') {
      if (this.previousAction !== 'sliding') {
        this.lastSlideDistance = snapshot.distance;
        if (this.style.slideTrails.enabled) {
          this.spawnSlideTrails(snapshot.player.x, snapshot.elapsed, this.style.slideTrails);
        }
      }
      let trailsSpawned = 0;
      while (
        this.style.slideTrails.enabled &&
        snapshot.distance - this.lastSlideDistance >= this.style.slideTrails.interval &&
        trailsSpawned < 4
      ) {
        this.lastSlideDistance += this.style.slideTrails.interval;
        this.spawnSlideTrails(snapshot.player.x, snapshot.elapsed, this.style.slideTrails);
        trailsSpawned += 1;
      }
    }

    for (const instance of this.instances) this.updateInstance(instance, snapshot.elapsed);
    this.previousAction = snapshot.player.action;
    this.previousElapsed = snapshot.elapsed;
  }

  private reset(): void {
    for (const instance of this.instances) {
      instance.active = false;
      instance.mesh.visible = false;
    }
    this.initialized = false;
    this.previousAction = 'grounded';
    this.previousElapsed = 0;
    this.lastSlideDistance = 0;
  }

  private spawnRing(x: number, elapsed: number, style: RingEffectStyle): void {
    if (!style.enabled) return;
    const instance = this.acquire('ring');
    this.activate(instance, {
      x,
      z: 0,
      elapsed,
      duration: style.duration,
      color: style.color,
      opacity: style.opacity,
      startScaleX: style.startScale,
      startScaleY: style.startScale,
      endScaleX: style.endScale,
      endScaleY: style.endScale,
      drift: style.drift,
    });
  }

  private spawnSlideTrails(x: number, elapsed: number, style: SlideTrailStyle): void {
    for (const direction of [-1, 1] as const) {
      const instance = this.acquire('trail');
      this.activate(instance, {
        x: x + direction * style.sideOffset,
        z: 0.5,
        elapsed,
        duration: style.duration,
        color: style.color,
        opacity: style.opacity,
        startScaleX: style.width,
        startScaleY: style.length,
        endScaleX: style.width * 0.35,
        endScaleY: style.length * 1.35,
        drift: style.drift,
      });
    }
  }

  private acquire(kind: EffectKind): EffectInstance {
    const reusable = this.instances.find((instance) => !instance.active && instance.kind === kind);
    if (reusable !== undefined) return reusable;

    const geometry = kind === 'ring' ? new RingGeometry(0.32, 0.48, 24) : new PlaneGeometry(1, 1);
    const effectMaterial = new MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    const mesh = new Mesh(geometry, effectMaterial);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.y = 0.025;
    mesh.renderOrder = 2;
    this.group.add(mesh);

    const instance: EffectInstance = {
      kind,
      mesh,
      active: false,
      startedAt: 0,
      duration: 1,
      startScaleX: 1,
      startScaleY: 1,
      endScaleX: 1,
      endScaleY: 1,
      startOpacity: 1,
      drift: 0,
      startZ: 0,
    };
    this.instances.push(instance);
    return instance;
  }

  private activate(
    instance: EffectInstance,
    values: Readonly<{
      x: number;
      z: number;
      elapsed: number;
      duration: number;
      color: number;
      opacity: number;
      startScaleX: number;
      startScaleY: number;
      endScaleX: number;
      endScaleY: number;
      drift: number;
    }>,
  ): void {
    instance.active = true;
    instance.startedAt = values.elapsed;
    instance.duration = values.duration;
    instance.startScaleX = values.startScaleX;
    instance.startScaleY = values.startScaleY;
    instance.endScaleX = values.endScaleX;
    instance.endScaleY = values.endScaleY;
    instance.startOpacity = values.opacity;
    instance.drift = values.drift;
    instance.startZ = values.z;
    instance.mesh.material.color.setHex(values.color);
    instance.mesh.position.set(values.x, 0.025, values.z);
    instance.mesh.scale.set(values.startScaleX, values.startScaleY, 1);
    instance.mesh.visible = true;
  }

  private updateInstance(instance: EffectInstance, elapsed: number): void {
    if (!instance.active) return;
    const progress = Math.min(1, Math.max(0, (elapsed - instance.startedAt) / instance.duration));
    if (progress === 1) {
      instance.active = false;
      instance.mesh.visible = false;
      return;
    }

    const eased = 1 - (1 - progress) ** 2;
    instance.mesh.scale.set(
      instance.startScaleX + (instance.endScaleX - instance.startScaleX) * eased,
      instance.startScaleY + (instance.endScaleY - instance.startScaleY) * eased,
      1,
    );
    instance.mesh.material.opacity = instance.startOpacity * (1 - progress) ** 2;
    instance.mesh.position.z = instance.startZ + instance.drift * progress;
  }
}
